import { HttpEventType, HttpResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { AwsService } from 'src/app/areas/fam/views/aws/aws.service';
import { SwModalframeComponent } from '../sw-modal-frame/sw-modal-frame.component';

declare const THREE: any;
declare const $: any;

/**
 * 三维模型预览公共组件。
 *
 * 需安装依赖：
 * npm install three@0.110.0 --save
 *
 * 使用方式1：只传 fileInfoId，组件自动弹框预览
 * <sw-three-model-preview [fileInfoId]="row.fileInfoId"></sw-three-model-preview>
 *
 * 使用方式2：通过 ViewChild 主动打开
 * 模板：
 * <sw-three-model-preview #threePreview [autoOpen]="false"></sw-three-model-preview>
 *
 * 组件代码：
 * @ViewChild('threePreview') threePreview: SwThreeModelPreviewComponent;
 * openPreview(fileInfoId: string) {
 *   this.threePreview.open(fileInfoId);
 * }
 */
@Component({
  selector: 'sw-three-model-preview',
  templateUrl: './sw-three-model-preview.component.html',
  styleUrls: ['./sw-three-model-preview.component.css']
})
export class SwThreeModelPreviewComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() title = '三维模型预览';
  @Input() fileInfoId = '';
  @Input() autoOpen = true;
  @Input() modalWidth = '84vw';
  @Input() modalHeight = '88vh';
  @Input() inlineMode = false;  // 内联模式：不弹框，直接在容器内渲染预览

  @ViewChild('modal')
  modal: SwModalframeComponent;

  @ViewChild('viewerContainer')
  viewerContainer: ElementRef;

  loading = false;
  statusText = '请传入 fileInfoId 后预览三维模型。';
  errorText = '';
  modelInfo: any = null;
  loadPercent = 0;
  viewerReady = false;
  backgroundMode: 'dark' | 'light' = 'dark';
  previewSettings = {
    exposure: 1.5,
    ambientIntensity: 1.8,
    mainLightIntensity: 1.1,
    fillLightIntensity: 0,
    topLightIntensity: 0,
    hemisphereIntensity: 0,
    hemisphereGroundColor: '#cfd6de',
    forceSrgbTextures: true,
    optimizePbr: false,
    metalnessMax: 1,
    roughnessMin: 0,
    roughnessMax: 1
  };
  private defaultPreviewSettings: any = Object.assign({}, this.previewSettings);
  private autoPreviewProfile: any = {
    avgLuminance: null,
    standardMaterialCount: 0,
    veryDark: false,
    extremeDark: false
  };

  private scene: any;
  private camera: any;
  private renderer: any;
  private controls: any;
  private currentModel: any;
  private animationId: number;
  private readonly defaultBackgroundColor = 0xf7f9fc;
  private readonly darkBackgroundColor = 0x1a1a2e;
  private ambientLight: any;
  private mainLight: any;
  private fillLight: any;
  private topLight: any;
  private hemisphereLight: any;
  private hdrSourceTexture: any;
  private hdrEnvironmentTexture: any;
  private hdrEnvironmentRenderTarget: any;
  private hdrEnvironmentLoaded = false;
  private modalOpened = false;
  private readonly resizeHandler = () => {
    this.handleResize();
  };

  constructor(
    private awsService: AwsService
  ) { }

  ngAfterViewInit() {
    if (this.autoOpen && this.trimFileInfoId()) {
      this.open();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.fileInfoId && !changes.fileInfoId.firstChange && this.autoOpen && this.trimFileInfoId()) {
      this.open();
    }
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.resizeHandler);
    this.stopAnimate();
    this.clearCurrentModel();
    if (this.controls && this.controls.dispose) {
      this.controls.dispose();
    }
    if (this.renderer) {
      this.renderer.dispose();
    }
    this.disposeHdrEnvironment();
  }

  open(fileInfoId?: string) {
    if (fileInfoId !== undefined) {
      this.fileInfoId = fileInfoId || '';
    }

    const currentFileInfoId = this.trimFileInfoId();
    if (!currentFileInfoId) {
      this.errorText = '请输入fileInfoId';
      this.statusText = '请传入 fileInfoId 后预览三维模型。';
      return;
    }

    this.resetPreviewBeforeLoad();
    this.ensureModalOpen();
    setTimeout(() => {
      if (!this.viewerReady) {
        this.initViewer();
      } else {
        this.handleResize();
        this.startAnimate();
      }
      this.generatePreview(currentFileInfoId);
    }, 240);
  }

  openDirect(fileInfoId: string, modelInfo?: any) {
    this.fileInfoId = fileInfoId || '';
    const currentFileInfoId = this.trimFileInfoId();
    if (!currentFileInfoId) {
      this.errorText = '请输入fileInfoId';
      this.statusText = '请传入 fileInfoId 后预览三维模型。';
      return;
    }

    this.resetPreviewBeforeLoad();
    this.modelInfo = modelInfo ? modelInfo : null;
    this.errorText = '';
    this.loading = true;
    this.statusText = '正在加载预览模型...';
    this.loadPercent = 0;

    this.ensureModalOpen();
    setTimeout(() => {
      if (!this.viewerReady) {
        this.initViewer();
      } else {
        this.handleResize();
        this.startAnimate();
      }
      this.loadPreviewFile(currentFileInfoId);
    }, 240);
  }

  close() {
    if (this.modal && this.modalOpened) {
      this.modal.toggle();
    }
    this.modalOpened = false;
    this.stopAnimate();
  }

  onModalClosed() {
    this.modalOpened = false;
    this.stopAnimate();
    this.cleanupModalArtifacts();
  }

  /**
   * 兜底清理 bootstrap 模态框残留：
   * 关闭后若遮罩或 modal-open 未移除，会挡住页面导致其他附件无法点击预览。
   */
  private cleanupModalArtifacts() {
    setTimeout(() => {
      try {
        const modalEl = this.modal && this.modal.ids ? document.getElementById(this.modal.ids) : null;
        if (modalEl && modalEl.classList.contains('in') && typeof $ !== 'undefined') {
          $('#' + this.modal.ids).modal('hide');
        }
        if (!document.querySelector('.modal.in')) {
          const backdrops = document.querySelectorAll('.modal-backdrop');
          for (let i = 0; i < backdrops.length; i++) {
            backdrops[i].parentNode.removeChild(backdrops[i]);
          }
          document.body.classList.remove('modal-open');
          document.body.style.paddingRight = '';
        }
      } catch (e) { }
    }, 350);
  }

  reload() {
    const currentFileInfoId = this.trimFileInfoId();
    if (!currentFileInfoId) {
      this.errorText = '请输入fileInfoId';
      return;
    }
    this.resetPreviewBeforeLoad();
    this.generatePreview(currentFileInfoId);
  }

  get contentHeight() {
    return '100%';
  }

  private generatePreview(fileInfoId: string) {
    if (!fileInfoId) {
      this.errorText = '请输入fileInfoId';
      this.statusText = '请传入 fileInfoId 后预览三维模型。';
      return;
    }

    this.loading = true;
    this.errorText = '';
    this.statusText = '正在根据 fileInfoId 请求后端转换三维模型...';
    this.loadPercent = 0;

    this.awsService.getThreePreviewInfoByFileInfoId(fileInfoId).subscribe(res => {
      if (res && res.code === 200 && res.data) {
        this.modelInfo = res.data;
        this.statusText = '转换成功，正在加载预览模型...';
        this.loadPreviewFile(fileInfoId);
        return;
      }

      this.loading = false;
      this.errorText = res && res.msg ? res.msg : '获取预览信息失败';
    }, () => {
      this.loading = false;
      this.errorText = '请求三维模型预览接口失败';
    });
  }

  toggleBackground(mode: 'dark' | 'light') {
    this.backgroundMode = mode;
    this.applyViewerBackground();
  }

  applyPreviewSettings() {
    this.syncPreviewSettingRanges();
    this.applyRendererSettings();
    this.applyLightSettings();
    this.refreshCurrentModelMaterials();
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  resetPreviewSettings() {
    this.previewSettings = Object.assign({}, this.defaultPreviewSettings);
    this.applyPreviewSettings();
  }

  private loadPreviewFile(previewId: string) {
    this.statusText = '正在下载预览模型文件...';
    this.loadPercent = 0;
    this.awsService.getThreePreviewFileByFileInfoId(previewId).subscribe(event => {
      if (!event) {
        this.loading = false;
        this.errorText = '获取预览模型文件失败';
        return;
      }

      if (event.type === HttpEventType.DownloadProgress) {
        const total = event.total || 0;
        if (total > 0) {
          this.loadPercent = Math.min(Math.round((event.loaded / total) * 90), 90);
        } else {
          this.loadPercent = Math.min(this.loadPercent + 5, 90);
        }
        this.statusText = '正在下载预览模型文件...';
        return;
      }

      if (event instanceof HttpResponse) {
        const body = event.body ? event.body : null;
        if (!body) {
          this.loading = false;
          this.errorText = '后端未返回可预览文件';
          return;
        }

        this.loadPercent = 92;
        this.statusText = '模型文件下载完成，正在读取数据...';
        this.loadModelFromBlob(body);
      }
    }, () => {
      this.loading = false;
      this.errorText = '获取预览模型文件失败';
    });
  }

  private initViewer() {
    if (!this.viewerContainer || !this.viewerContainer.nativeElement || typeof THREE === 'undefined') {
      this.errorText = 'Three.js 脚本未加载成功';
      return;
    }

    const container = this.viewerContainer.nativeElement;
    const width = container.clientWidth || 960;
    const height = container.clientHeight || 540;

    this.scene = new THREE.Scene();
    this.applyViewerBackground();

    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 10000);
    this.camera.position.set(0, 2, 6);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    this.renderer.setPixelRatio(window.devicePixelRatio || 1);
    this.renderer.setSize(width, height);
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    if (this.renderer.toneMapping !== undefined && THREE.ACESFilmicToneMapping !== undefined) {
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    }
    if (this.renderer.physicallyCorrectLights !== undefined) {
      this.renderer.physicallyCorrectLights = false;
    }
    container.style.position = 'relative';
    container.style.zIndex = '2';
    container.style.overflow = 'hidden';
    this.renderer.domElement.style.display = 'block';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.renderer.domElement.style.position = 'relative';
    this.renderer.domElement.style.zIndex = '3';
    this.renderer.domElement.style.pointerEvents = 'auto';
    container.appendChild(this.renderer.domElement);
    this.viewerReady = true;

    this.ambientLight = new THREE.AmbientLight(0xffffff, this.previewSettings.ambientIntensity);
    this.scene.add(this.ambientLight);

    this.mainLight = new THREE.DirectionalLight(0xffffff, this.previewSettings.mainLightIntensity);
    this.mainLight.position.set(5, 10, 7.5);
    this.scene.add(this.mainLight);

    this.fillLight = new THREE.DirectionalLight(0xffffff, this.previewSettings.fillLightIntensity);
    this.fillLight.position.set(-10, 6, -8);
    this.scene.add(this.fillLight);

    this.topLight = new THREE.DirectionalLight(0xffffff, this.previewSettings.topLightIntensity);
    this.topLight.position.set(0, 14, 2);
    this.scene.add(this.topLight);

    this.hemisphereLight = new THREE.HemisphereLight(0xffffff, this.previewSettings.hemisphereGroundColor, this.previewSettings.hemisphereIntensity);
    this.scene.add(this.hemisphereLight);

    this.applyRendererSettings();
    this.applyLightSettings();
    this.loadHdrEnvironment();

    this.controls = new THREE.TrackballControls(this.camera, this.renderer.domElement);
    this.controls.rotateSpeed = 2.2;
    this.controls.zoomSpeed = 1.4;
    this.controls.panSpeed = 0.8;
    this.controls.staticMoving = false;
    this.controls.dynamicDampingFactor = 0.12;
    this.controls.noRotate = false;
    this.controls.noZoom = false;
    this.controls.noPan = false;
    this.controls.handleResize();
    this.controls.target.set(0, 0, 0);

    window.addEventListener('resize', this.resizeHandler);
    this.startAnimate();
  }

  private resetPreviewBeforeLoad() {
    this.clearCurrentModel();
    this.modelInfo = null;
    this.errorText = '';
    this.loadPercent = 0;
    if (this.controls && this.controls.target) {
      this.controls.target.set(0, 0, 0);
      if (this.controls.update) {
        this.controls.update();
      }
    }
    if (this.camera) {
      this.camera.position.set(0, 2, 6);
      this.camera.lookAt(0, 0, 0);
    }
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  private ensureModalOpen() {
    if (!this.modal || this.modalOpened) {
      return;
    }
    this.modal.toggle();
    this.modalOpened = true;
  }

  private applyViewerBackground() {
    const backgroundColor = this.backgroundMode === 'light' ? this.defaultBackgroundColor : this.darkBackgroundColor;
    if (this.scene) {
      this.scene.background = new THREE.Color(backgroundColor);
    }
    if (this.viewerContainer && this.viewerContainer.nativeElement) {
      this.viewerContainer.nativeElement.style.background = this.backgroundMode === 'light' ? '#f7f9fc' : '#1a1a2e';
    }
  }

  private loadModelFromBlob(blobData: Blob) {
    if (!this.viewerReady) {
      this.loading = false;
      this.errorText = '三维预览容器初始化失败';
      return;
    }

    const reader = new FileReader();
    reader.onprogress = event => {
      if (!event) {
        return;
      }
      const total = event.total || blobData.size || 0;
      if (total > 0) {
        const percent = Math.round((event.loaded / total) * 6);
        this.loadPercent = Math.min(92 + percent, 98);
      }
      this.statusText = '正在读取模型数据...';
    };
    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      this.loadPercent = 99;
      this.statusText = '正在解析模型场景...';
      this.loadModelFromArrayBuffer(arrayBuffer);
    };
    reader.onerror = () => {
      this.loading = false;
      this.errorText = '读取预览模型数据失败';
    };
    reader.readAsArrayBuffer(blobData);
  }

  private loadModelFromArrayBuffer(arrayBuffer: ArrayBuffer) {
    const loader = new THREE.GLTFLoader();

    loader.parse(arrayBuffer, '', gltf => {
      this.clearCurrentModel();
      this.currentModel = gltf.scene;
      if (!this.currentModel) {
        this.loading = false;
        this.errorText = '模型文件解析成功，但场景为空';
        return;
      }

      this.currentModel.traverse(node => {
        if (node && node.isMesh) {
          node.castShadow = true;
          node.receiveShadow = true;
          if (node.material) {
            const materials = Array.isArray(node.material) ? node.material : [node.material];
            materials.forEach(material => this.optimizePreviewMaterial(material));
          }
        }
      });

      this.applyAutoPreviewSettings(this.currentModel);
      this.normalizeModel(this.currentModel);
      this.scene.add(this.currentModel);
      this.fitCameraToObject(this.currentModel);
      this.loadPercent = 100;
      this.loading = false;
      this.statusText = '模型加载完成，可以拖拽旋转查看。';
    }, error => {
      this.loading = false;
      this.errorText = error && error.message ? error.message : '模型加载失败';
    });
  }

  private applyAutoPreviewSettings(object: any) {
    const recommendedSettings = this.getRecommendedPreviewSettings(object);
    this.previewSettings = Object.assign({}, recommendedSettings);
    this.applyPreviewSettings();
  }

  private getRecommendedPreviewSettings(object: any) {
    const recommendedSettings = Object.assign({}, this.previewSettings);
    let materialLuminanceTotal = 0;
    let materialLuminanceCount = 0;
    let standardMaterialCount = 0;

    if (object && object.traverse) {
      object.traverse(node => {
        if (!node || !node.isMesh || !node.material) {
          return;
        }
        const materials = Array.isArray(node.material) ? node.material : [node.material];
        materials.forEach(material => {
          if (!material) {
            return;
          }
          if (material.type === 'MeshStandardMaterial' || material.type === 'MeshPhysicalMaterial') {
            standardMaterialCount++;
          }
          const luminance = this.readMaterialLuminance(material);
          if (luminance !== null) {
            materialLuminanceTotal += luminance;
            materialLuminanceCount++;
          }
        });
      });
    }

    const avgLuminance = materialLuminanceCount ? materialLuminanceTotal / materialLuminanceCount : null;
    this.autoPreviewProfile = {
      avgLuminance: avgLuminance,
      standardMaterialCount: standardMaterialCount,
      veryDark: avgLuminance !== null && avgLuminance < 0.18,
      extremeDark: avgLuminance !== null && avgLuminance < 0.1
    };

    if (avgLuminance !== null) {
      if (avgLuminance < 0.1) {
        recommendedSettings.exposure = 1.85;
        recommendedSettings.ambientIntensity = 1.2;
        recommendedSettings.mainLightIntensity = 1.55;
        recommendedSettings.fillLightIntensity = 0.32;
        recommendedSettings.topLightIntensity = 0.14;
        recommendedSettings.hemisphereIntensity = 0.24;
      } else if (avgLuminance < 0.18) {
        recommendedSettings.exposure = 1.55;
        recommendedSettings.ambientIntensity = 0.95;
        recommendedSettings.mainLightIntensity = 1.3;
        recommendedSettings.fillLightIntensity = 0.2;
        recommendedSettings.topLightIntensity = 0.08;
      } else if (avgLuminance < 0.32) {
        recommendedSettings.exposure = 1.5;
        recommendedSettings.ambientIntensity = 1.8;
        recommendedSettings.mainLightIntensity = 1.1;
        recommendedSettings.fillLightIntensity = 0.08;
        recommendedSettings.topLightIntensity = 0.04;
      } else if (avgLuminance > 0.78) {
        recommendedSettings.exposure = 0.92;
        recommendedSettings.ambientIntensity = 0.42;
        recommendedSettings.mainLightIntensity = 0.88;
      } else if (avgLuminance > 0.64) {
        recommendedSettings.exposure = 0.97;
        recommendedSettings.ambientIntensity = 0.46;
        recommendedSettings.mainLightIntensity = 0.95;
      }
    }

    if (standardMaterialCount > 0) {
      recommendedSettings.hemisphereIntensity = avgLuminance !== null && avgLuminance < 0.32 ? 0.12 : 0.06;
      if (avgLuminance === null) {
        recommendedSettings.fillLightIntensity = 0.08;
      }
    }

    return recommendedSettings;
  }

  private readMaterialLuminance(material: any): number | null {
    if (!material) {
      return null;
    }
    if (material.map) {
      const textureLuminance = this.readTextureLuminance(material.map);
      if (textureLuminance !== null) {
        return textureLuminance;
      }
    }
    if (material.color && material.color.r !== undefined && material.color.g !== undefined && material.color.b !== undefined) {
      return this.toLuminance(material.color.r, material.color.g, material.color.b);
    }
    return null;
  }

  private readTextureLuminance(texture: any): number | null {
    if (!texture || !texture.image) {
      return null;
    }

    const image = texture.image;

    if (image.data && image.data.length) {
      const pixelCount = Math.floor(image.data.length / 4);
      if (!pixelCount) {
        return null;
      }
      const step = Math.max(1, Math.floor(pixelCount / 64));
      let luminanceTotal = 0;
      let sampleCount = 0;
      for (let index = 0; index < pixelCount; index += step) {
        const offset = index * 4;
        luminanceTotal += this.toLuminance(
          this.normalizeColorChannel(image.data[offset]),
          this.normalizeColorChannel(image.data[offset + 1]),
          this.normalizeColorChannel(image.data[offset + 2])
        );
        sampleCount++;
      }
      return sampleCount ? luminanceTotal / sampleCount : null;
    }

    const width = image.naturalWidth || image.videoWidth || image.width;
    const height = image.naturalHeight || image.videoHeight || image.height;
    if (!width || !height) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 16;
    canvas.height = 16;
    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }

    try {
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height).data;
      let luminanceTotal = 0;
      let sampleCount = 0;
      for (let offset = 0; offset < imageData.length; offset += 4) {
        luminanceTotal += this.toLuminance(
          imageData[offset] / 255,
          imageData[offset + 1] / 255,
          imageData[offset + 2] / 255
        );
        sampleCount++;
      }
      return sampleCount ? luminanceTotal / sampleCount : null;
    } catch (error) {
      return null;
    }
  }

  private normalizeColorChannel(value: number) {
    if (value === null || value === undefined || isNaN(value)) {
      return 0;
    }
    if (value > 1) {
      return Math.max(0, Math.min(1, value / 255));
    }
    return Math.max(0, Math.min(1, value));
  }

  private toLuminance(r: number, g: number, b: number) {
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  private normalizeModel(object: any) {
    const box = new THREE.Box3().setFromObject(object);
    const size = box.getSize(new THREE.Vector3());
    const maxSize = Math.max(size.x, size.y, size.z);

    if (!maxSize || !isFinite(maxSize)) {
      return;
    }

    const targetSize = 4;
    const scale = targetSize / maxSize;
    object.scale.set(scale, scale, scale);

    const normalizedBox = new THREE.Box3().setFromObject(object);
    const normalizedCenter = normalizedBox.getCenter(new THREE.Vector3());
    object.position.x -= normalizedCenter.x;
    object.position.y -= normalizedCenter.y;
    object.position.z -= normalizedCenter.z;
  }

  private fitCameraToObject(object: any) {
    const box = new THREE.Box3().setFromObject(object);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxSize = Math.max(size.x, size.y, size.z);
    const distance = Math.max(maxSize * 2.2, 3);

    this.camera.position.set(center.x, center.y, center.z + distance);
    this.camera.near = Math.max(distance / 1000, 0.1);
    this.camera.far = Math.max(distance * 100, 1000);
    this.camera.updateProjectionMatrix();

    if (this.controls) {
      this.controls.target.copy(center);
      this.camera.lookAt(center);
      this.controls.update();
    }
  }

  private optimizePreviewMaterial(material: any) {
    if (!material) {
      return;
    }

    const isExtremeDarkModel = !!this.autoPreviewProfile.extremeDark;
    const isVeryDarkModel = !!this.autoPreviewProfile.veryDark;

    if (material.side !== undefined) {
      material.side = THREE.DoubleSide;
    }

    if (this.previewSettings.forceSrgbTextures && material.map && material.map.encoding !== undefined) {
      material.map.encoding = THREE.sRGBEncoding;
      material.map.needsUpdate = true;
    }
    if (this.previewSettings.forceSrgbTextures && material.emissiveMap && material.emissiveMap.encoding !== undefined) {
      material.emissiveMap.encoding = THREE.sRGBEncoding;
      material.emissiveMap.needsUpdate = true;
    }

    if (material.metalness !== undefined && material.__previewOriginalMetalness === undefined) {
      material.__previewOriginalMetalness = material.metalness;
    }
    if (material.roughness !== undefined && material.__previewOriginalRoughness === undefined) {
      material.__previewOriginalRoughness = material.roughness;
    }
    if (material.emissive && material.__previewOriginalEmissiveHex === undefined) {
      material.__previewOriginalEmissiveHex = material.emissive.getHex ? material.emissive.getHex() : 0x000000;
    }
    if (material.emissiveIntensity !== undefined && material.__previewOriginalEmissiveIntensity === undefined) {
      material.__previewOriginalEmissiveIntensity = material.emissiveIntensity;
    }
    if (material.__previewOriginalEmissiveMap === undefined) {
      material.__previewOriginalEmissiveMap = material.emissiveMap || null;
    }
    if (material.__previewOriginalEnvMap === undefined) {
      material.__previewOriginalEnvMap = material.envMap || null;
    }
    if (material.envMapIntensity !== undefined && material.__previewOriginalEnvMapIntensity === undefined) {
      material.__previewOriginalEnvMapIntensity = material.envMapIntensity;
    }
    if (material.toneMapped !== undefined && material.__previewOriginalToneMapped === undefined) {
      material.__previewOriginalToneMapped = material.toneMapped;
    }

    if (this.previewSettings.optimizePbr) {
      if (material.metalness !== undefined) {
        material.metalness = Math.min(material.__previewOriginalMetalness, this.previewSettings.metalnessMax);
      }
      if (material.roughness !== undefined) {
        material.roughness = Math.min(
          Math.max(material.__previewOriginalRoughness, this.previewSettings.roughnessMin),
          this.previewSettings.roughnessMax
        );
      }
    } else {
      if (material.metalness !== undefined && material.__previewOriginalMetalness !== undefined) {
        material.metalness = material.__previewOriginalMetalness;
      }
      if (material.roughness !== undefined && material.__previewOriginalRoughness !== undefined) {
        material.roughness = material.__previewOriginalRoughness;
      }
    }

    // 摄影测量/贴图类模型如果被错误导出为高金属度，预览里会整体发黑。
    // 这里仅在“有颜色贴图、没有金属度贴图、metalness 极高”的情况下做预览层兜底。
    const shouldUseTextureFirstPreview = (material.type === 'MeshStandardMaterial' || material.type === 'MeshPhysicalMaterial')
      && material.map
      && !material.metalnessMap
      && material.metalness !== undefined
      && material.metalness >= 0.95;

    if (shouldUseTextureFirstPreview) {
      material.metalness = 0;
      if (material.roughness !== undefined) {
        material.roughness = 1;
      }
      if (material.emissive && material.emissive.setHex) {
        material.emissive.setHex(0xffffff);
      }
      if (material.emissiveMap !== undefined) {
        material.emissiveMap = material.map;
      }
      if (material.emissiveIntensity !== undefined) {
        material.emissiveIntensity = 1;
      }
      if (material.envMapIntensity !== undefined) {
        material.envMapIntensity = 0;
      }
      if (material.toneMapped !== undefined) {
        material.toneMapped = false;
      }
      if (material.transparent === true && material.opacity === 1) {
        material.transparent = false;
      }
    } else if (material.toneMapped !== undefined && material.__previewOriginalToneMapped !== undefined) {
      material.toneMapped = material.__previewOriginalToneMapped;
    }

    if (isExtremeDarkModel || isVeryDarkModel) {
      if (material.metalness !== undefined) {
        material.metalness = Math.min(material.metalness, isExtremeDarkModel ? 0.02 : 0.08);
      }
      if (material.roughness !== undefined) {
        material.roughness = Math.max(material.roughness, isExtremeDarkModel ? 0.92 : 0.82);
      }
      if (material.emissive && material.emissive.setHex) {
        material.emissive.setHex(0xffffff);
      }
      if (material.map && material.emissiveMap !== undefined) {
        material.emissiveMap = material.map;
      } else if (material.emissiveMap !== undefined) {
        material.emissiveMap = material.__previewOriginalEmissiveMap;
      }
      if (material.emissiveIntensity !== undefined) {
        material.emissiveIntensity = isExtremeDarkModel ? 0.65 : 0.28;
      }
    } else {
      if (material.emissive && material.emissive.setHex && material.__previewOriginalEmissiveHex !== undefined) {
        material.emissive.setHex(material.__previewOriginalEmissiveHex);
      }
      if (material.emissiveMap !== undefined) {
        material.emissiveMap = material.__previewOriginalEmissiveMap;
      }
      if (material.emissiveIntensity !== undefined && material.__previewOriginalEmissiveIntensity !== undefined) {
        material.emissiveIntensity = material.__previewOriginalEmissiveIntensity;
      }
    }
    if (!shouldUseTextureFirstPreview && this.hdrEnvironmentLoaded && this.hdrEnvironmentTexture && material.envMap !== undefined) {
      material.envMap = this.hdrEnvironmentTexture;
      if (material.envMapIntensity !== undefined) {
        if (isExtremeDarkModel) {
          material.envMapIntensity = 2.4;
        } else if (isVeryDarkModel) {
          material.envMapIntensity = 1.8;
        } else {
          material.envMapIntensity = Math.max(material.__previewOriginalEnvMapIntensity || 1, 1.15);
        }
      }
    } else if (material.envMap !== undefined) {
      material.envMap = material.__previewOriginalEnvMap;
      if (material.envMapIntensity !== undefined && material.__previewOriginalEnvMapIntensity !== undefined && !shouldUseTextureFirstPreview) {
        material.envMapIntensity = material.__previewOriginalEnvMapIntensity;
      }
    }

    if (this.previewSettings.optimizePbr && material.envMapIntensity !== undefined) {
      material.envMapIntensity = Math.max(material.envMapIntensity || 1, 1.2);
    }

    material.needsUpdate = true;
  }

  private syncPreviewSettingRanges() {
    if (this.previewSettings.roughnessMin > this.previewSettings.roughnessMax) {
      this.previewSettings.roughnessMax = this.previewSettings.roughnessMin;
    }
  }

  private applyRendererSettings() {
    if (!this.renderer) {
      return;
    }
    if (this.renderer.toneMappingExposure !== undefined) {
      this.renderer.toneMappingExposure = this.previewSettings.exposure;
    }
  }

  private applyLightSettings() {
    if (this.ambientLight) {
      this.ambientLight.intensity = this.previewSettings.ambientIntensity;
    }
    if (this.mainLight) {
      this.mainLight.intensity = this.previewSettings.mainLightIntensity;
    }
    if (this.fillLight) {
      this.fillLight.intensity = this.previewSettings.fillLightIntensity;
    }
    if (this.topLight) {
      this.topLight.intensity = this.previewSettings.topLightIntensity;
    }
    if (this.hemisphereLight) {
      this.hemisphereLight.intensity = this.previewSettings.hemisphereIntensity;
      this.hemisphereLight.groundColor = new THREE.Color(this.previewSettings.hemisphereGroundColor);
    }
  }

  private refreshCurrentModelMaterials() {
    if (!this.currentModel) {
      return;
    }
    this.currentModel.traverse(node => {
      if (node && node.isMesh && node.material) {
        const materials = Array.isArray(node.material) ? node.material : [node.material];
        materials.forEach(material => this.optimizePreviewMaterial(material));
      }
    });
  }

  private loadHdrEnvironment() {
    return;
  }

  private disposeHdrEnvironment() {
    if (this.scene && this.scene.environment === this.hdrEnvironmentTexture) {
      this.scene.environment = null;
    }
    if (this.hdrEnvironmentRenderTarget && this.hdrEnvironmentRenderTarget.dispose) {
      this.hdrEnvironmentRenderTarget.dispose();
    }
    if (this.hdrSourceTexture && this.hdrSourceTexture.dispose) {
      this.hdrSourceTexture.dispose();
    } else if (this.hdrEnvironmentTexture && this.hdrEnvironmentTexture.dispose) {
      this.hdrEnvironmentTexture.dispose();
    }
    this.hdrSourceTexture = null;
    this.hdrEnvironmentTexture = null;
    this.hdrEnvironmentRenderTarget = null;
    this.hdrEnvironmentLoaded = false;
  }

  private clearCurrentModel() {
    if (!this.currentModel || !this.scene) {
      return;
    }

    this.scene.remove(this.currentModel);
    this.currentModel.traverse(node => {
      if (node && node.isMesh) {
        if (node.geometry && node.geometry.dispose) {
          node.geometry.dispose();
        }
        if (node.material) {
          if (Array.isArray(node.material)) {
            node.material.forEach(material => {
              if (material && material.dispose) {
                material.dispose();
              }
            });
          } else if (node.material.dispose) {
            node.material.dispose();
          }
        }
      }
    });
    this.currentModel = null;
  }

  private animationRunning = false;

  /**
   * 启动渲染循环（仅在预览打开期间运行，避免 rAF 常驻导致 zone 变更检测死循环）
   */
  private startAnimate() {
    if (this.animationRunning) {
      return;
    }
    this.animationRunning = true;
    const loop = () => {
      if (!this.animationRunning) {
        return;
      }
      this.animationId = requestAnimationFrame(loop);
      if (this.controls) {
        this.controls.update();
      }
      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    };
    this.animationId = requestAnimationFrame(loop);
  }

  /**
   * 停止渲染循环，释放 rAF，避免持续触发变更检测导致页面卡死
   */
  private stopAnimate() {
    this.animationRunning = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = undefined;
    }
  }

  private handleResize() {
    if (!this.viewerContainer || !this.camera || !this.renderer) {
      return;
    }

    const container = this.viewerContainer.nativeElement;
    const width = container.clientWidth || 960;
    const height = container.clientHeight || 540;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    if (this.controls && this.controls.handleResize) {
      this.controls.handleResize();
    }
  }

  private trimFileInfoId() {
    return this.fileInfoId ? this.fileInfoId.trim() : '';
  }
}
